import React, { useState } from 'react';
import styled from 'styled-components';

import { DashboardTableCard } from './DashboardTableCard';
import { GridActionsCellItem, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid';
import { VisibilityOutlined } from '@mui/icons-material';
import { ViewError } from '@components/Modals/Logs/ViewError';
import { Tooltip } from '@common/Tooltip';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '@store/index';
import { useNavigate } from 'react-router-dom';
import { refresh } from '@store/async';
import { useSnackbar } from 'notistack';
import { Dayjs } from 'dayjs';
import { listAdminWorkflows, listAdminWorkspaces } from '@requests/admin/reporting.requests';
import { DashboardCardType, DashboardContainer, FullScreenTable } from './Dashboard';

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  height: 100%;
  gap: 1rem;
  padding-bottom: 2rem;
`;

interface Props {
  startDate: Dayjs | null;
  endDate: Dayjs | null;
}

export const DashboardSearch: React.FC<Props> = ({ startDate, endDate }) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const { enqueueSnackbar } = useSnackbar();

  const [fullScreenCard, setFullScreenCard] = useState<string | null>(null);
  const [logError, setLogError] = useState<string | null>(null);

  const openAccountPage = async (path: string, user_id: string, org_id: string) => {
    localStorage.setItem('internalUserId', user_id);
    localStorage.setItem('internalOrgId', org_id);

    const result = await dispatch(refresh());

    if (result.type === 'auth/refresh/fulfilled' && !result?.payload?.error) {
      navigate(path, { replace: true });
    }

    if (result?.type === 'auth/refresh/rejected') {
      enqueueSnackbar('Failed to login to users account.', {
        variant: 'error',
        autoHideDuration: 10000,
      });
    }
  };

  const cards: DashboardCardType[] = [
    {
      queryKey: 'admin_workspaces',
      queryFn: listAdminWorkspaces,
      title: 'Workspaces',
      columns: [
        {
          field: 'service_name',
          headerName: 'Name',
          flex: 100,
          sortable: true,
        },
        {
          field: 'service_id',
          headerName: 'Domain',
          flex: 130,
          sortable: true,
          renderCell: (params: GridRenderCellParams<any>) => {
            const row = params.row;

            return <pre>{`/execute/${row.service_id}/`}</pre>;
          },
        },
        {
          field: 'created_by',
          headerName: 'Created By',
          flex: 100,
          sortable: true,
        },
        {
          field: 'created_at',
          headerName: 'Created At (UTC)',
          flex: 100,
          sortable: true,
          renderCell: (params: GridRenderCellParams<any>) => {
            const { row } = params;

            return <div>{new Date(row.updated_at).toLocaleString()}</div>;
          },
        },
        {
          field: 'actions',
          type: 'actions',
          headerName: 'Actions',
          width: 100,
          cellClassName: 'actions',
          getActions: (params: GridRowParams<any>) => {
            const { row } = params;
            return [
              <Tooltip title='View Workspace' key='view' arrow placement='top'>
                <GridActionsCellItem
                  icon={<VisibilityOutlined />}
                  label='View Workspace'
                  onClick={() => openAccountPage(`/${row.service_id}/workflow`, row.user_id, row.org_id)}
                  color='inherit'
                />
              </Tooltip>,
            ];
          },
        },
      ],
    },
    {
      queryKey: 'admin_workflows',
      queryFn: listAdminWorkflows,
      title: 'Workflows',
      columns: [
        {
          field: 'name',
          headerName: 'Name',
          flex: 120,
          sortable: true,
        },
        {
          field: 'service_path',
          headerName: 'Endpoint',
          flex: 280,
          sortable: true,
          renderCell: (params: GridRenderCellParams<any>) => {
            const row = params.row;

            return <pre>{`/execute/${row.service_id}/${row.service_path}`}</pre>;
          },
        },
        {
          field: 'created_by',
          headerName: 'Created By',
          flex: 70,
          sortable: true,
        },
        fullScreenCard && {
          field: 'created_at',
          headerName: 'Created At (UTC)',
          flex: 110,
          sortable: true,
          renderCell: (params: GridRenderCellParams<any>) => {
            const { row } = params;

            return <div>{new Date(row.updated_at).toLocaleString()}</div>;
          },
        },
        fullScreenCard && {
          field: 'updated_at',
          headerName: 'Updated At (UTC)',
          flex: 110,
          sortable: true,
          renderCell: (params: GridRenderCellParams<any>) => {
            const { row } = params;

            return <div>{new Date(row.updated_at).toLocaleString()}</div>;
          },
        },
        {
          field: 'actions',
          type: 'actions',
          headerName: 'Actions',
          width: 100,
          cellClassName: 'actions',
          getActions: (params: GridRowParams<any>) => {
            const { row } = params;
            return [
              <Tooltip title='View Workflow' key='view' arrow placement='top'>
                <GridActionsCellItem
                  icon={<VisibilityOutlined />}
                  label='View'
                  onClick={() => openAccountPage(`/${row.service_id}/workflow/${row.flow_id}`, row.user_id, row.org_id)}
                  color='inherit'
                />
              </Tooltip>,
            ];
          },
        },
      ].filter(Boolean),
    },
  ];

  if (fullScreenCard) {
    const card = cards.find((card) => card.queryKey === fullScreenCard);

    if (card) {
      return (
        <DashboardContainer>
          <ViewError error={logError} setError={setLogError} />
          <FullScreenTable>
            <DashboardTableCard
              card={card}
              onClose={() => setFullScreenCard(null)}
              setFullScreenCard={setFullScreenCard}
              startDate={startDate}
              endDate={endDate}
            />
          </FullScreenTable>
        </DashboardContainer>
      );
    }
  }

  return (
    <DashboardContainer>
      <ViewError error={logError} setError={setLogError} />
      <GridContainer>
        <DashboardTableCard
          card={cards[0]}
          setFullScreenCard={setFullScreenCard}
          startDate={startDate}
          endDate={endDate}
        />
        <DashboardTableCard
          card={cards[1]}
          setFullScreenCard={setFullScreenCard}
          startDate={startDate}
          endDate={endDate}
        />
      </GridContainer>
    </DashboardContainer>
  );
};
